/* ============================================================
   Админка · Настройки федерации.
   ============================================================ */
import { useEffect, useState } from 'react';
import { useFedContent } from '../content';
import { useAppStore } from '../../store';
import { AdmCard, AdmPrimaryBtn } from './ui';

export default function AdmSettings() {
  const settings = useFedContent((s) => s.settings);
  const saveSettings = useFedContent((s) => s.saveSettings);
  const counters = useFedContent((s) => s.counters);
  const saveCounters = useFedContent((s) => s.saveCounters);
  const pushAll = useFedContent((s) => s.pushAll);
  const fire = useAppStore((s) => s.fire);
  const [draft, setDraft] = useState({ ...settings });
  const [countersDraft, setCountersDraft] = useState(counters.map((c) => ({ ...c })));
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [countersDirty, setCountersDirty] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settingsDirty) setDraft({ ...settings });
  }, [settings, settingsDirty]);

  useEffect(() => {
    if (!countersDirty) setCountersDraft(counters.map((c) => ({ ...c })));
  }, [counters, countersDirty]);

  const editCounter = (i, patch) => {
    setCountersDirty(true);
    setCountersDraft((list) => list.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  };

  const publishAll = async () => {
    if (pushing) return;
    setPushing(true);
    const ok = await pushAll();
    setPushing(false);
    if (ok) fire && fire('Весь контент опубликован на сервер', 'ok');
  };

  // «Сохранено» — только если сервер принял: pushKey сам покажет ошибку
  const saveWith = (fn, okMsg, onSaved) => async () => {
    if (saving) return;
    setSaving(true);
    const ok = await fn();
    setSaving(false);
    if (ok) {
      onSaved && onSaved();
      fire && fire(okMsg, 'ok');
    }
  };

  // обычная функция (не компонент) — чтобы инпуты не пересоздавались и не теряли фокус
  const field = (label, k, type = 'text') => (
    <label key={k} className="flex flex-col gap-[7px]">
      <span className="fed-label">{label}</span>
      <input className="fed-input" type={type} value={draft[k] || ''} onChange={(e) => { setSettingsDirty(true); setDraft({ ...draft, [k]: e.target.value }); }} />
    </label>
  );

  return (
    <div className="fed-admin-view">
      <AdmCard style={{ padding: 24, maxWidth: 720 }}>
        <div className="fed-grid-form">
          {field('Название федерации', 'name')}
          {field('Регион', 'region')}
          {field('Телефон', 'phone')}
          {field('Telegram', 'telegram')}
          {field('Email', 'email', 'email')}
          {field('Город', 'city')}
        </div>
        <label className="flex flex-col gap-[7px]" style={{ marginTop: 16 }}>
          <span className="fed-label">О федерации</span>
          <textarea className="fed-input" rows={4} value={draft.about || ''} onChange={(e) => { setSettingsDirty(true); setDraft({ ...draft, about: e.target.value }); }} />
        </label>
        <div className="flex justify-end" style={{ marginTop: 20 }}>
          <AdmPrimaryBtn icon="check" onClick={saveWith(() => saveSettings(draft), 'Настройки сохранены', () => setSettingsDirty(false))}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </AdmPrimaryBtn>
        </div>
      </AdmCard>

      <AdmCard style={{ padding: 24, maxWidth: 720, marginTop: 16 }}>
        <div className="font-display font-semibold text-fg" style={{ fontSize: 16 }}>Счётчики на главной</div>
        <p className="text-fg-muted" style={{ fontSize: 13, marginTop: 6, marginBottom: 16 }}>
          Блок с цифрами под тикером на главной странице.
        </p>
        <div className="flex flex-col gap-4">
          {countersDraft.map((c, i) => (
            <div key={i} style={{ padding: 12, border: '1px solid var(--fed-line)', borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="fed-grid-form" style={{ gridTemplateColumns: '100px 100px' }}>
                <label className="flex flex-col gap-[5px]">
                  <span className="fed-label">Число</span>
                  <input className="fed-input" type="number" value={c.value} onChange={(e) => editCounter(i, { value: Number(e.target.value) })} />
                </label>
                <label className="flex flex-col gap-[5px]">
                  <span className="fed-label">Суффикс</span>
                  <input className="fed-input" value={c.suffix || ''} onChange={(e) => editCounter(i, { suffix: e.target.value })} />
                </label>
              </div>
              <div className="fed-grid-form">
                <label className="flex flex-col gap-[5px]">
                  <span className="fed-label">Подпись RU</span>
                  <input className="fed-input" value={c.label_ru || ''} onChange={(e) => editCounter(i, { label_ru: e.target.value })} />
                </label>
                <label className="flex flex-col gap-[5px]">
                  <span className="fed-label">Подпись KZ</span>
                  <input className="fed-input" value={c.label_kz || ''} onChange={(e) => editCounter(i, { label_kz: e.target.value })} />
                </label>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end" style={{ marginTop: 16 }}>
          <AdmPrimaryBtn icon="check" onClick={saveWith(() => saveCounters(countersDraft), 'Счётчики сохранены', () => setCountersDirty(false))}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </AdmPrimaryBtn>
        </div>
      </AdmCard>

      <AdmCard style={{ padding: 24, maxWidth: 720, marginTop: 16 }}>
        <div className="font-display font-semibold text-fg" style={{ fontSize: 16 }}>Публикация контента</div>
        <p className="text-fg-muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 8 }}>
          Отправляет на сервер все разделы витрины (новости, турниры, партнёры, проекты,
          победители, команда, документы, настройки) в их текущем виде. Используйте один раз
          после деплоя, чтобы наполнить пустую базу, — дальше каждое изменение публикуется
          автоматически.
        </p>
        <div className="flex justify-end" style={{ marginTop: 16 }}>
          <AdmPrimaryBtn icon="external" onClick={publishAll}>
            {pushing ? 'Публикуем…' : 'Опубликовать весь контент на сервер'}
          </AdmPrimaryBtn>
        </div>
      </AdmCard>
    </div>
  );
}
